import { FormEvent, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetClubBoatByIdQuery,
  useCreateClubBoatMutation,
  useUpdateClubBoatMutation,
} from '@/services/ClubBoatManagementService';
import { BoatPayload, InspectionEntry } from '@/types/boat-management.types';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { textFieldValidation } from '@/utils/validations';
import { useGetAllMembersQuery } from '@/services/ClubService';
import { MemberResponse } from '@/types/club-management.types';
import { ErrorApiResponse } from '@/types/api.types';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useIntl, IntlShape } from 'react-intl';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import NumberInput from '@/components/inputControls/NumberInput';

interface BoatFormPageProps {
  mode: 'create' | 'edit' | 'view';
}

interface BoatFormState {
  // Core identification
  name: string;
  type: string;
  registrationNumber: string;
  certificateNumber: string;

  // Dimensions
  unit: string;
  length: string;
  width: string;
  draft: string;
  height: string;
  weight: string;
  displacement: string;

  // Inspections
  inspections: InspectionEntry[];

  // Club & ownership
  holder: string;
  linkedMember: string;

  // Additional details
  manufacturer: string;
  manufacturerCountry: string;
  hullNumber: string;
  hullColor: string;
  insuranceCompany: string;
  yearOfManufacture: string;
  berths: string;
  sewageSystem: string;
  sewageTankSize: string;
  fuelTankCapacity: string;
  freshWaterTankCapacity: string;
  engine: string;
}

const boatTypes = [
  'Sailboat',
  'Motorboat',
  'Yacht',
  'Catamaran',
  'Fishing Boat',
  'Speedboat',
] as const;

type BoatNumericField =
  | 'length'
  | 'width'
  | 'draft'
  | 'height'
  | 'weight'
  | 'displacement'
  | 'berths'
  | 'sewageTankSize'
  | 'fuelTankCapacity'
  | 'freshWaterTankCapacity';

const normalizeNumericFieldValue = (value: string) =>
  value.trim().replace(/\s/g, '').replace(/,/g, '.');

const buildBoatFormSchema = (intl: IntlShape) =>
  Yup.object().shape({
    name: textFieldValidation(
      intl ? intl.formatMessage({ id: 'BOAT.FORM.NAME' }) : 'Boat Name',
      2,
      50,
      true,
      intl
    ),
    type: Yup.string().required(
      intl
        ? intl.formatMessage({ id: 'BOAT.VALIDATION.TYPE_REQUIRED' })
        : 'Boat type is required'
    ),
    unit: Yup.string().required(
      intl
        ? intl.formatMessage({ id: 'BOAT.VALIDATION.UNIT_REQUIRED' })
        : 'Unit is required'
    ),
    length: Yup.string()
      .required(
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.LENGTH_REQUIRED' })
          : 'Length is required'
      )
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.LENGTH_FORMAT' })
          : "Length must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      ),
    width: Yup.string()
      .required(
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.WIDTH_REQUIRED' })
          : 'Width is required'
      )
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.WIDTH_FORMAT' })
          : "Width must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      ),
    draft: Yup.string()
      .required(
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.DRAFT_REQUIRED' })
          : 'Draft is required'
      )
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.DRAFT_FORMAT' })
          : "Draft must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      ),
    height: Yup.string()
      .required(
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.HEIGHT_REQUIRED' })
          : 'Height is required'
      )
      .matches(
        /^\d+(?:[.,]\d{1,2})?$/,
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.HEIGHT_FORMAT' })
          : "Height must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
      ),
    registrationNumber: textFieldValidation(
      intl
        ? intl.formatMessage({ id: 'BOAT.FORM.REGISTRATION_NUMBER' })
        : 'Registration Number',
      3,
      20,
      true,
      intl
    ),
    inspections: Yup.array().of(
      Yup.object().shape({
        year: Yup.string().required(
          intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: intl.formatMessage({ id: 'BOAT.FORM.INSPECTION_YEAR' }) }
          )
        ),
        medium: Yup.string().required(
          intl.formatMessage(
            { id: 'VALIDATION.REQUIRED' },
            { field: intl.formatMessage({ id: 'BOAT.FORM.MEDIUM' }) }
          )
        ),
      })
    ),
    linkedMember: Yup.string().nullable(),
    // New fields are optional for now to avoid breaking existing flows
    certificateNumber: Yup.string().notRequired(),
    weight: Yup.string()
      .notRequired()
      .test(
        'weight-format',
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.WEIGHT_FORMAT' })
          : 'Weight must be a number',
        (value) => !value || /^\d+(?:[.,]\d{0,2})?$/.test(value)
      ),
    displacement: Yup.string()
      .notRequired()
      .test(
        'displacement-format',
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.DISPLACEMENT_FORMAT' })
          : 'Displacement must be a number',
        (value) => !value || /^\d+(?:[.,]\d{0,2})?$/.test(value)
      ),
    yearOfManufacture: Yup.string()
      .notRequired()
      .test(
        'year-format',
        intl
          ? intl.formatMessage({ id: 'BOAT.VALIDATION.YEAR_FORMAT' })
          : 'Year must be 4 digits',
        (value) => !value || /^\d{4}$/.test(value)
      ),
  });

const BoatFormPage = ({ mode }: BoatFormPageProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clubId = searchParams.get('clubId');
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();

  const isViewMode = mode === 'view';

  const [formState, setFormState] = useState<BoatFormState>({
    // Identification
    name: '',
    type: '',
    registrationNumber: '',
    certificateNumber: '',

    // Dimensions
    unit: '',
    length: '',
    width: '',
    draft: '',
    height: '',
    weight: '',
    displacement: '',

    // Inspections
    inspections: [],

    // Club & ownership
    holder: '',
    linkedMember: '',

    // Additional details
    manufacturer: '',
    manufacturerCountry: '',
    hullNumber: '',
    hullColor: '',
    insuranceCompany: '',
    yearOfManufacture: '',
    berths: '',
    sewageSystem: '',
    sewageTankSize: '',
    fuelTankCapacity: '',
    freshWaterTankCapacity: '',
    engine: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const boatFormSchema = useMemo(() => buildBoatFormSchema(intl), [intl]);

  // Fetch all club members without pagination for the linked member dropdown.
  const { data: clubMembersResponse, isLoading: isLoadingClubMembers } =
    useGetAllMembersQuery(
      {
        club_id: clubId || '',
        status: 1, // Only active members
      },
      {
        skip: !clubId,
      }
    );

  const clubMembers: MemberResponse[] = useMemo(
    () =>
      Array.isArray(clubMembersResponse?.data) ? clubMembersResponse.data : [],
    [clubMembersResponse?.data]
  );

  const { data: boatResponse, isLoading: isBoatLoading } =
    useGetClubBoatByIdQuery(id!, {
      skip: !id || mode === 'create',
    });

  const [createBoat] = useCreateClubBoatMutation();
  const [updateBoat] = useUpdateClubBoatMutation();

  useEffect(() => {
    if (boatResponse && (mode === 'edit' || mode === 'view')) {
      const boatData = boatResponse;
      setFormState((prev) => ({
        ...prev,
        name: boatData.name || '',
        type: boatData.type || '',
        registrationNumber: boatData.registration_number || '',
        length: String(boatData.length) || '',
        width: String(boatData.width) || '',
        draft: String(boatData.draft) || '',
        height: String(boatData.height) || '',
        unit: boatData.unit || '',
        // Map other fields from boatData if available
        certificateNumber: boatData.certificate_number || '',
        weight: boatData.weight || '',
        displacement: boatData.displacement || '',
        inspections: boatData.inspections || [],
        holder: boatData.holder || '',
        linkedMember: boatData.boater_id || '',
        manufacturer: boatData.manufacturer || '',
        manufacturerCountry: boatData.manufacturer_country || '',
        hullNumber: boatData.hull_number || '',
        hullColor: boatData.hull_color || '',
        insuranceCompany: boatData.insurance_company || '',
        yearOfManufacture: boatData.year_of_manufacture || '',
        berths: boatData.berths || '',
        sewageSystem: boatData.sewage_system || '',
        sewageTankSize: boatData.sewage_tank_size || '',
        fuelTankCapacity: boatData.fuel_tank_capacity || '',
        freshWaterTankCapacity: boatData.fresh_water_tank_capacity || '',
        engine: boatData.engine || '',
      }));
    }
  }, [boatResponse, mode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (
      name === 'length' ||
      name === 'width' ||
      name === 'draft' ||
      name === 'height' ||
      name === 'weight' ||
      name === 'displacement' ||
      name === 'sewageTankSize' ||
      name === 'fuelTankCapacity' ||
      name === 'freshWaterTankCapacity'
    ) {
      const sanitized = value.replace(/[^\d.,]/g, '');
      setFormState((prev) => ({ ...prev, [name]: sanitized }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (name === 'unit') {
      setFormState((prev) => ({ ...prev, unit: value as 'm' | 'ft' }));
      setErrors((prev) => ({ ...prev, unit: '' }));
      return;
    }

    setFormState((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleNumericInputChange = (name: BoatNumericField) => {
    return (value: number | undefined) => {
      const nextValue =
        value === undefined ? '' : String(value).replace('.', ',');
      setFormState((prev) => ({ ...prev, [name]: nextValue }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
    };
  };

  const handleInspectionChange = (
    index: number,
    field: keyof InspectionEntry,
    value: string
  ) => {
    setFormState((prev) => {
      const inspections = [...prev.inspections];
      inspections[index] = { ...inspections[index], [field]: value };
      return { ...prev, inspections };
    });
  };

  const handleAddInspection = () => {
    setFormState((prev) => ({
      ...prev,
      inspections: [
        ...prev.inspections,
        { year: '', inspectionClass: '', notes: '', medium: '' },
      ],
    }));
  };

  const handleRemoveInspection = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      inspections: prev.inspections.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isViewMode) {
      navigate(-1);
      return;
    }

    try {
      setIsSubmitting(true);
      await boatFormSchema.validate(formState, { abortEarly: false });
      setErrors({});

      const normalizedLength = normalizeNumericFieldValue(formState.length);
      const normalizedWidth = normalizeNumericFieldValue(formState.width);
      const normalizedDraft = normalizeNumericFieldValue(formState.draft);
      const normalizedHeight = normalizeNumericFieldValue(formState.height);
      const normalizedWeight = formState.weight
        ? normalizeNumericFieldValue(formState.weight)
        : undefined;
      const normalizedDisplacement = formState.displacement
        ? normalizeNumericFieldValue(formState.displacement)
        : undefined;
      const normalizedBerths = formState.berths
        ? normalizeNumericFieldValue(formState.berths)
        : undefined;
      const normalizedSewageTankSize = formState.sewageTankSize
        ? normalizeNumericFieldValue(formState.sewageTankSize)
        : undefined;
      const normalizedFuelTankCapacity = formState.fuelTankCapacity
        ? normalizeNumericFieldValue(formState.fuelTankCapacity)
        : undefined;
      const normalizedFreshWaterTankCapacity = formState.freshWaterTankCapacity
        ? normalizeNumericFieldValue(formState.freshWaterTankCapacity)
        : undefined;

      const payload: BoatPayload = {
        name: formState.name,
        type: formState.type,
        registration_number: formState.registrationNumber,
        length: normalizedLength,
        width: normalizedWidth,
        draft: normalizedDraft,
        height: normalizedHeight,
        unit: formState.unit as 'm' | 'ft',
        boater_id: formState.linkedMember || null,
        club_id: clubId || '',
        is_custom_boat_model: true,
        boat_model_name: formState.name,
        // Add additional fields that were in the form state
        certificate_number: formState.certificateNumber,
        weight: normalizedWeight,
        displacement: normalizedDisplacement,
        inspections: formState.inspections,
        holder: formState.holder,
        manufacturer: formState.manufacturer,
        manufacturer_country: formState.manufacturerCountry,
        hull_number: formState.hullNumber,
        hull_color: formState.hullColor,
        insurance_company: formState.insuranceCompany,
        year_of_manufacture: formState.yearOfManufacture,
        berths: normalizedBerths,
        sewage_system: formState.sewageSystem,
        sewage_tank_size: normalizedSewageTankSize,
        fuel_tank_capacity: normalizedFuelTankCapacity,
        fresh_water_tank_capacity: normalizedFreshWaterTankCapacity,
        engine: formState.engine,
      };

      if (mode === 'create') {
        await createBoat(payload).unwrap();
        enqueueSnackbar(
          intl.formatMessage({ id: 'BOAT.MESSAGE.CREATE_SUCCESS' }),
          { variant: 'success' }
        );
      } else {
        await updateBoat({ boat_id: id!, data: payload }).unwrap();
        enqueueSnackbar(
          intl.formatMessage({ id: 'BOAT.MESSAGE.UPDATE_SUCCESS' }),
          { variant: 'success' }
        );
      }

      navigate(-1);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        setErrors(validationErrors);
        enqueueSnackbar(intl.formatMessage({ id: 'BOAT.ERROR.FORM' }), {
          variant: 'error',
        });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message ||
          intl.formatMessage({ id: 'BOAT.ERROR.SUBMIT' });
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    mode === 'create'
      ? intl.formatMessage({ id: 'BOAT.FORM.CREATE.TITLE' })
      : mode === 'edit'
        ? intl.formatMessage({ id: 'BOAT.FORM.EDIT.TITLE' })
        : intl.formatMessage({ id: 'BOAT.FORM.VIEW.TITLE' });

  const description =
    mode === 'create'
      ? intl.formatMessage({ id: 'BOAT.FORM.CREATE.DESCRIPTION' })
      : mode === 'edit'
        ? intl.formatMessage({ id: 'BOAT.FORM.EDIT.DESCRIPTION' })
        : intl.formatMessage({ id: 'BOAT.FORM.VIEW.DESCRIPTION' });

  // Build a small, reusable list of year options for year-only fields so that
  // year selection is consistent and mobile-friendly.
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 50 }, (_, index) => {
        const year = currentYear - index;
        return year;
      }),
    [currentYear]
  );

  const inspectionYearOptions = useMemo(
    () =>
      Array.from({ length: 11 }, (_, index) => {
        const year = currentYear + index;
        return year;
      }),
    [currentYear]
  );

  if (isBoatLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle text={title} />
            <ToolbarDescription>{description}</ToolbarDescription>
          </ToolbarHeading>
        </Toolbar>
      </Container>

      <Container>
        <div className="card responsive-form">
          <div className="card-header flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-2">
              <KeenIcon icon="ship" className="text-xl" />
              <div className="text-base font-semibold text-gray-800">
                {intl.formatMessage({ id: 'COMMON.BOAT.DETAILS' })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="mt-2 md:mt-0 relative">
                <label htmlFor="language-select" className="sr-only">
                  {intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
                </label>
                <select
                  id="language-select"
                  className="select select-bordered pl-8 pr-8 h-10 md:h-9 leading-normal"
                  value={currentLanguage.code}
                  onChange={(e) => {
                    const selectedLang = I18N_LANGUAGES.find(
                      (lang) => lang.code === e.target.value
                    );
                    if (selectedLang) {
                      changeLanguage(selectedLang);
                    }
                  }}
                  aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
                >
                  {I18N_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={
                  mode === 'view'
                    ? intl.formatMessage({ id: 'BOAT.ARIA.BACK' })
                    : intl.formatMessage({ id: 'BOAT.ARIA.CANCEL_BACK' })
                }
              >
                <KeenIcon
                  type="filled"
                  icon="cross"
                  className="text-gray-500"
                />
                {mode === 'view'
                  ? intl.formatMessage({ id: 'COMMON.BACK' })
                  : intl.formatMessage({ id: 'COMMON.CANCEL' })}
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit} noValidate>
            <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
              {/* Boat identification */}
              <div className="md:col-span-2">
                <h2 className="text-base font-semibold text-gray-800">
                  {intl.formatMessage({ id: 'BOAT.SECTION.IDENTIFICATION' })}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {intl.formatMessage({
                    id: 'BOAT.SECTION.IDENTIFICATION_DESC',
                  })}
                </p>
              </div>

              <div>
                <label
                  htmlFor="boat-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.NAME' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="boat-name"
                  name="name"
                  type="text"
                  autoComplete="off"
                  value={formState.name}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.name && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.NAME.PLACEHOLDER',
                  })}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-type"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.TYPE' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="boat-type"
                  name="type"
                  value={formState.type}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.type && 'border-red-500'
                  )}
                >
                  <option value="" disabled selected hidden>
                    {intl.formatMessage({ id: 'BOAT.FORM.TYPE' })}
                  </option>
                  <option value="">
                    {
                      intl
                        .formatMessage({ id: 'COMMON.SELECT_STATUS' })
                        .split(' ')[0]
                    }{' '}
                    {intl.formatMessage({ id: 'BOAT.FORM.TYPE' })}
                  </option>
                  {boatTypes.map((t) => (
                    <option key={t} value={t}>
                      {intl.formatMessage({
                        id: `BOAT.TYPE.${t.toUpperCase().replace(/\s+/g, '_')}`,
                      })}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="text-red-500 text-xs mt-1">{errors.type}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-registration"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.REGISTRATION_NUMBER' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="boat-registration"
                  name="registrationNumber"
                  type="text"
                  autoComplete="off"
                  value={formState.registrationNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.registrationNumber && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.REGISTRATION_NUMBER.PLACEHOLDER',
                  })}
                />
                {errors.registrationNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.registrationNumber}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-certificate-number"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.CERTIFICATE_NUMBER' })}
                </label>
                <input
                  id="boat-certificate-number"
                  name="certificateNumber"
                  type="text"
                  autoComplete="off"
                  value={formState.certificateNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.CERTIFICATE_NUMBER.PLACEHOLDER',
                  })}
                />
              </div>

              {/* Dimensions */}
              <div className="md:col-span-2 pt-2">
                <h2 className="text-base font-semibold text-gray-800">
                  {intl.formatMessage({ id: 'BOAT.SECTION.DIMENSIONS' })}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {intl.formatMessage({ id: 'BOAT.SECTION.DIMENSIONS_DESC' })}
                </p>
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-unit"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.UNIT' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="boat-unit"
                  name="unit"
                  value={formState.unit}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'select select-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.unit && 'border-red-500'
                  )}
                >
                  <option value="" disabled selected hidden>
                    {intl.formatMessage({ id: 'BOAT.FORM.UNIT' })}
                  </option>
                  <option value="">
                    {
                      intl
                        .formatMessage({ id: 'COMMON.SELECT_YEAR' })
                        .split(' ')[0]
                    }{' '}
                    {intl.formatMessage({ id: 'BOAT.FORM.UNIT' })}
                  </option>
                  <option value="m">{`${intl.formatMessage({ id: 'COMMON.METERS' })} (m)`}</option>
                  <option value="ft">{`${intl.formatMessage({ id: 'COMMON.FEET' })} (ft)`}</option>
                </select>
                {errors.unit && (
                  <p className="text-red-500 text-xs mt-1">{errors.unit}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-length"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.LENGTH' })} (
                  {formState.unit}) <span className="text-red-500">*</span>
                </label>
                <NumberInput
                  id="boat-length"
                  name="length"
                  value={formState.length}
                  onValueChange={handleNumericInputChange('length')}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.length && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage(
                    { id: 'BOAT.FORM.LENGTH.PLACEHOLDER' },
                    { unit: formState.unit }
                  )}
                  min={0}
                  step={0.01}
                  maxLength={10}
                />
                {errors.length && (
                  <p className="text-red-500 text-xs mt-1">{errors.length}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-width"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.BEAM' })} (
                  {formState.unit})<span className="text-red-500">*</span>
                </label>
                <NumberInput
                  id="boat-width"
                  name="width"
                  value={formState.width}
                  onValueChange={handleNumericInputChange('width')}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.width && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage(
                    { id: 'BOAT.FORM.BEAM.PLACEHOLDER' },
                    { unit: formState.unit }
                  )}
                  min={0}
                  step={0.01}
                  maxLength={10}
                />
                {errors.width && (
                  <p className="text-red-500 text-xs mt-1">{errors.width}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-draft"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.DRAFT' })} (
                  {formState.unit}) <span className="text-red-500">*</span>
                </label>
                <NumberInput
                  id="boat-draft"
                  name="draft"
                  value={formState.draft}
                  onValueChange={handleNumericInputChange('draft')}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.draft && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage(
                    { id: 'BOAT.FORM.DRAFT.PLACEHOLDER' },
                    { unit: formState.unit }
                  )}
                  min={0}
                  step={0.01}
                  maxLength={10}
                />
                {errors.draft && (
                  <p className="text-red-500 text-xs mt-1">{errors.draft}</p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="boat-height"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.HEIGHT' })} (
                  {formState.unit}) <span className="text-red-500">*</span>
                </label>
                <NumberInput
                  id="boat-height"
                  name="height"
                  value={formState.height}
                  onValueChange={handleNumericInputChange('height')}
                  disabled={isViewMode || isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-11 rounded-lg border-gray-300',
                    errors.height && 'border-red-500'
                  )}
                  placeholder={intl.formatMessage(
                    { id: 'BOAT.FORM.HEIGHT.PLACEHOLDER' },
                    { unit: formState.unit }
                  )}
                  min={0}
                  step={0.01}
                  maxLength={10}
                />
                {errors.height && (
                  <p className="text-red-500 text-xs mt-1">{errors.height}</p>
                )}
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="boat-weight"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.WEIGHT' })}
                </label>
                <NumberInput
                  id="boat-weight"
                  name="weight"
                  value={formState.weight}
                  onValueChange={handleNumericInputChange('weight')}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.WEIGHT.PLACEHOLDER',
                  })}
                  min={0}
                  step={0.01}
                />
                {errors.weight && (
                  <p className="text-red-500 text-xs mt-1">{errors.weight}</p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-displacement"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.DISPLACEMENT' })}
                </label>
                <NumberInput
                  id="boat-displacement"
                  name="displacement"
                  value={formState.displacement}
                  onValueChange={handleNumericInputChange('displacement')}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.DISPLACEMENT.PLACEHOLDER',
                  })}
                  min={0}
                  step={0.01}
                />
                {errors.displacement && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.displacement}
                  </p>
                )}
              </div>

              {/* Inspections */}
              <div className="col-span-1 md:col-span-2 pt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-800">
                      {intl.formatMessage({ id: 'BOAT.SECTION.INSPECTIONS' })}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      {intl.formatMessage({
                        id: 'BOAT.SECTION.INSPECTIONS_DESC',
                      })}
                    </p>
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={handleAddInspection}
                      className="btn btn-xs btn-light"
                      disabled={isSubmitting}
                    >
                      {intl.formatMessage({
                        id: 'BOAT.FORM.INSPECTIONS.ADD_BUTTON',
                      })}
                    </button>
                  )}
                </div>
              </div>

              {formState.inspections.map((inspection, index) => (
                <div
                  key={index}
                  className="col-span-1 md:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 border border-dashed border-gray-200 rounded-xl p-4"
                >
                  <div className="col-span-1">
                    <label
                      htmlFor={`inspection-year-${index}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'BOAT.FORM.INSPECTION_YEAR' })}
                    </label>
                    <select
                      id={`inspection-year-${index}`}
                      value={inspection.year}
                      onChange={(e) =>
                        handleInspectionChange(index, 'year', e.target.value)
                      }
                      disabled={isViewMode || isSubmitting}
                      className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'COMMON.SELECT_YEAR' })}
                      </option>
                      {inspectionYearOptions.map((year) => (
                        <option key={year} value={String(year)}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-1">
                    <label
                      htmlFor={`inspection-class-${index}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'BOAT.FORM.INSPECTION_CLASS' })}
                    </label>
                    <input
                      id={`inspection-class-${index}`}
                      type="text"
                      autoComplete="off"
                      value={inspection.inspectionClass}
                      onChange={(e) =>
                        handleInspectionChange(
                          index,
                          'inspectionClass',
                          e.target.value
                        )
                      }
                      disabled={isViewMode || isSubmitting}
                      className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                      placeholder={intl.formatMessage({
                        id: 'BOAT.FORM.INSPECTION_CLASS.PLACEHOLDER',
                      })}
                    />
                  </div>

                  <div className="col-span-1">
                    <label
                      htmlFor={`inspection-medium-${index}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'BOAT.FORM.MEDIUM' })}
                    </label>
                    <select
                      id={`inspection-medium-${index}`}
                      value={inspection.medium}
                      onChange={(e) =>
                        handleInspectionChange(index, 'medium', e.target.value)
                      }
                      disabled={isViewMode || isSubmitting}
                      className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'BOAT.FORM.MEDIUM.SELECT' })}
                      </option>
                      <option value="digital">
                        {intl.formatMessage({ id: 'BOAT.FORM.MEDIUM.DIGITAL' })}
                      </option>
                      <option value="paper">
                        {intl.formatMessage({ id: 'BOAT.FORM.MEDIUM.PAPER' })}
                      </option>
                    </select>
                  </div>

                  <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex flex-col gap-2">
                    <label
                      htmlFor={`inspection-notes-${index}`}
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({ id: 'BOAT.FORM.NOTES' })}
                    </label>
                    <textarea
                      id={`inspection-notes-${index}`}
                      value={inspection.notes}
                      onChange={(e) =>
                        handleInspectionChange(index, 'notes', e.target.value)
                      }
                      disabled={isViewMode || isSubmitting}
                      className="textarea textarea-bordered w-full min-h-[80px] rounded-lg border-gray-300 resize-y"
                      placeholder={intl.formatMessage({
                        id: 'BOAT.FORM.INSPECTION_NOTES.PLACEHOLDER',
                      })}
                    />
                    {!isViewMode && formState.inspections.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveInspection(index)}
                        className="btn btn-xs btn-light text-red-600 mt-1 self-start"
                        disabled={isSubmitting}
                      >
                        {intl.formatMessage({ id: 'COMMON.REMOVE' })}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Club & ownership */}
              <div className="col-span-1 md:col-span-2 pt-2">
                <h2 className="text-base font-semibold text-gray-800">
                  {intl.formatMessage({ id: 'BOAT.SECTION.CLUB_OWNERSHIP' })}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {intl.formatMessage({
                    id: 'BOAT.SECTION.CLUB_OWNERSHIP_DESC',
                  })}
                </p>
              </div>
              <div className="col-span-1">
                <label
                  htmlFor="boat-holder"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.HOLDER_OWNER' })}
                </label>
                <input
                  id="boat-holder"
                  name="holder"
                  type="text"
                  autoComplete="off"
                  value={formState.holder}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.HOLDER_OWNER.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                <span
                  id="boat-linked-member-label"
                  className="text-sm font-medium text-gray-700"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.LINKED_MEMBER' })}{' '}
                </span>
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                  <select
                    id="boat-linked-member"
                    aria-labelledby="boat-linked-member-label"
                    name="linkedMember"
                    value={formState.linkedMember}
                    onChange={handleInputChange}
                    disabled={
                      isViewMode ||
                      isSubmitting ||
                      !clubId ||
                      isLoadingClubMembers ||
                      clubMembers.length === 0
                    }
                    className="select select-bordered w-full lg:w-1/2 h-11 rounded-lg border-gray-300"
                  >
                    <option value="" disabled selected hidden>
                      {intl.formatMessage({ id: 'BOAT.FORM.LINKED_MEMBER' })}
                    </option>
                    {(!clubId ||
                      isLoadingClubMembers ||
                      clubMembers.length === 0) && (
                      <option value="">
                        {clubId
                          ? isLoadingClubMembers
                            ? intl.formatMessage({
                                id: 'BOAT.LINKED_MEMBER.LOADING',
                              })
                            : intl.formatMessage({
                                id: 'BOAT.LINKED_MEMBER.EMPTY',
                              })
                          : intl.formatMessage({
                              id: 'BOAT.LINKED_MEMBER.NO_CLUB',
                            })}
                      </option>
                    )}
                    {clubMembers?.map((member) => (
                      <option key={member?.id} value={member.id}>
                        {member.user?.name || member.user?.email}
                      </option>
                    ))}
                  </select>
                  {errors.linkedMember && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.linkedMember}
                    </p>
                  )}
                </div>
              </div>

              {/* Additional details */}
              <div className="col-span-1 md:col-span-2 pt-2">
                <h2 className="text-base font-semibold text-gray-800">
                  {intl.formatMessage({
                    id: 'BOAT.SECTION.ADDITIONAL_DETAILS',
                  })}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {intl.formatMessage({
                    id: 'BOAT.SECTION.ADDITIONAL_DETAILS_DESC',
                  })}
                </p>
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-manufacturer"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.MANUFACTURER' })}
                </label>
                <input
                  id="boat-manufacturer"
                  name="manufacturer"
                  type="text"
                  autoComplete="off"
                  value={formState.manufacturer}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.MANUFACTURER.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-manufacturer-country"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.MANUFACTURER_COUNTRY' })}
                </label>
                <input
                  id="boat-manufacturer-country"
                  name="manufacturerCountry"
                  type="text"
                  autoComplete="off"
                  value={formState.manufacturerCountry}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.MANUFACTURER_COUNTRY.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-hull-number"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.HULL_NUMBER' })}
                </label>
                <input
                  id="boat-hull-number"
                  name="hullNumber"
                  type="text"
                  autoComplete="off"
                  value={formState.hullNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.HULL_NUMBER.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-hull-color"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.HULL_COLOR' })}
                </label>
                <input
                  id="boat-hull-color"
                  name="hullColor"
                  type="text"
                  autoComplete="off"
                  value={formState.hullColor}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.HULL_COLOR.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-insurance-company"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.INSURANCE_COMPANY' })}
                </label>
                <input
                  id="boat-insurance-company"
                  name="insuranceCompany"
                  type="text"
                  autoComplete="off"
                  value={formState.insuranceCompany}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.INSURANCE_COMPANY.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-year-of-manufacture"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.YEAR_OF_MANUFACTURE' })}
                </label>
                <select
                  id="boat-year-of-manufacture"
                  name="yearOfManufacture"
                  value={formState.yearOfManufacture}
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                >
                  <option value="">
                    {intl.formatMessage({ id: 'COMMON.SELECT_YEAR' })}
                  </option>
                  {yearOptions.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
                {errors.yearOfManufacture && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.yearOfManufacture}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-berths"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.BERTHS' })}
                </label>
                <NumberInput
                  id="boat-berths"
                  name="berths"
                  value={formState.berths}
                  onValueChange={handleNumericInputChange('berths')}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.BERTHS.PLACEHOLDER',
                  })}
                  min={0}
                  step={1}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-sewage-system"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.SEWAGE_SYSTEM' })}
                </label>
                <input
                  id="boat-sewage-system"
                  name="sewageSystem"
                  type="text"
                  value={formState.sewageSystem}
                  autoComplete="off"
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.SEWAGE_SYSTEM.PLACEHOLDER',
                  })}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-sewage-tank-size"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.SEWAGE_TANK_SIZE' })}
                </label>
                <NumberInput
                  id="boat-sewage-tank-size"
                  name="sewageTankSize"
                  value={formState.sewageTankSize}
                  onValueChange={handleNumericInputChange('sewageTankSize')}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.SEWAGE_TANK_SIZE.PLACEHOLDER',
                  })}
                  min={0}
                  step={0.01}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-fuel-tank-capacity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.FUEL_TANK_CAPACITY' })}
                </label>
                <NumberInput
                  id="boat-fuel-tank-capacity"
                  name="fuelTankCapacity"
                  value={formState.fuelTankCapacity}
                  onValueChange={handleNumericInputChange('fuelTankCapacity')}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.FUEL_TANK_CAPACITY.PLACEHOLDER',
                  })}
                  min={0}
                  step={0.01}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-fresh-water-tank-capacity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({
                    id: 'BOAT.FORM.FRESH_WATER_TANK_CAPACITY',
                  })}
                </label>
                <NumberInput
                  id="boat-fresh-water-tank-capacity"
                  name="freshWaterTankCapacity"
                  value={formState.freshWaterTankCapacity}
                  onValueChange={handleNumericInputChange(
                    'freshWaterTankCapacity'
                  )}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.FRESH_WATER_TANK_CAPACITY.PLACEHOLDER',
                  })}
                  min={0}
                  step={0.01}
                />
              </div>

              <div className="col-span-1">
                <label
                  htmlFor="boat-engine"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'BOAT.FORM.ENGINE' })}
                </label>
                <input
                  id="boat-engine"
                  name="engine"
                  type="text"
                  value={formState.engine}
                  autoComplete="off"
                  onChange={handleInputChange}
                  disabled={isViewMode || isSubmitting}
                  className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                  placeholder={intl.formatMessage({
                    id: 'BOAT.FORM.ENGINE.PLACEHOLDER',
                  })}
                />
              </div>
            </div>

            {!isViewMode && (
              <div className="card-footer flex flex-col sm:flex-row justify-end gap-2.5 mt-5">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn btn-sm btn-light w-full sm:w-auto border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 order-2 sm:order-1"
                  disabled={isSubmitting}
                >
                  {intl.formatMessage({ id: 'COMMON.CANCEL' })}
                </button>
                <button
                  type="submit"
                  className="btn btn-sm btn-primary w-full sm:w-auto px-6 py-2 order-1 sm:order-2"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting
                    ? intl.formatMessage({ id: 'BOAT.BUTTON.SAVING' })
                    : mode === 'create'
                      ? intl.formatMessage({ id: 'COMMON.CREATE' })
                      : intl.formatMessage({ id: 'COMMON.SAVE' })}
                </button>
              </div>
            )}
          </form>
        </div>
      </Container>
    </>
  );
};

export default BoatFormPage;
